import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Trash2, FileCheck2, Leaf, Loader2, Plus, CalendarSync } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { TisTracker, TisBadge } from "@/components/TisTracker";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { myTrips, deleteTrip, billingStatus, cancelSubscription, startCheckout, apiError } from "@/lib/api";

const fmtDate = (iso, locale) => new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

const SubscriptionCard = ({ sub, onChange }) => {
  const { t, locale, payCurrency, single, monthly } = useLang();
  const [busy, setBusy] = useState(false);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(t(apiError(e))); setBusy(false); return; }
    setBusy(false);
  };
  const subscribe = () => act(async () => { const { checkout_url } = await startCheckout("monthly", null, payCurrency); window.location.href = checkout_url; });
  const cancel = () => act(async () => { onChange(await cancelSubscription()); toast.success(t("Abbonamento disdetto: resta attivo fino a fine periodo.")); });

  return (
    <div data-testid="subscription-card" className="rounded-3xl border border-emerald-100 bg-white p-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700"><CalendarSync className="h-4 w-4" />{t("Abbonamento PDF")}</p>
      {sub?.active ? (
        <>
          <p className="mt-4 font-display text-2xl font-semibold text-gray-900">{t("Attivo · {monthly}/mese", { monthly })}</p>
          <p data-testid="subscription-usage" className="mt-2 text-sm text-gray-600">{t("{u} di {q} PDF usati questo mese", { u: sub.used, q: sub.quota })}</p>
          <div className="mt-3 h-2 rounded-full bg-emerald-50"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(sub.used / sub.quota) * 100}%` }} /></div>
          <p className="mt-3 text-xs text-gray-500">{sub.cancel_at_period_end ? t("Termina il ") : t("Si rinnova il ")}{new Date(sub.period_end * 1000).toLocaleDateString(locale)}</p>
          {!sub.cancel_at_period_end && (
            <button data-testid="cancel-subscription-button" onClick={cancel} disabled={busy} className="mt-5 text-sm font-semibold text-gray-500 underline-offset-4 hover:text-red-600 hover:underline">{t("Disdici abbonamento")}</button>
          )}
        </>
      ) : (
        <>
          <p className="mt-4 font-display text-2xl font-semibold text-gray-900">{t("Nessun abbonamento")}</p>
          <p className="mt-2 text-sm text-gray-600">{t("Con {monthly}/mese scarichi fino a 20 PDF al mese. In alternativa ogni PDF costa {single}.", { monthly, single })}</p>
          <button data-testid="subscribe-button" onClick={subscribe} disabled={busy}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("Abbonati a {monthly}/mese", { monthly })}
          </button>
        </>
      )}
    </div>
  );
};

const TripCard = ({ t, onDelete }) => {
  const { t: tr, locale } = useLang();
  return (
  <div data-testid={`my-trip-${t.id}`} className="group flex flex-col rounded-3xl border border-emerald-100 bg-white p-6 transition-shadow hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-display text-xl font-semibold text-gray-900">{t.destination || tr("Itinerario in preparazione")}</p>
        <p className="mt-1 flex items-center gap-1 text-sm text-gray-500"><MapPin className="h-3.5 w-3.5" />{t.country || "—"}</p>
      </div>
      {t.tis != null && <TisBadge id={`my-trip-tis-${t.id}`} score={t.tis} label={t.tis_label} />}
    </div>
    {t.tis_label && <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700"><Leaf className="h-3.5 w-3.5" />{t.tis_label}</p>}
    <p className="mt-3 line-clamp-2 flex-1 text-sm text-gray-600">{t.prompt}</p>
    <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
      {t.days && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{tr("{n} giorni", { n: t.days })}</span>}
      {t.budget && <span>{t.budget}</span>}
      {t.pdf_unlocked && <span className="flex items-center gap-1 text-emerald-700"><FileCheck2 className="h-3.5 w-3.5" />{tr("PDF sbloccato")}</span>}
      <span>{fmtDate(t.created_at, locale)}</span>
    </div>
    <div className="mt-5 flex items-center justify-between">
      <Link to={`/viaggio/${t.id}`} data-testid={`open-trip-${t.id}`} className="rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">{tr("Apri itinerario")}</Link>
      <button data-testid={`delete-trip-${t.id}`} onClick={() => onDelete(t)} aria-label={tr("Elimina")} className="grid h-10 w-10 place-items-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
  );
};

export default function Account() {
  const { user, openAuth } = useAuth();
  const { t } = useLang();
  const [trips, setTrips] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!user) return;
    myTrips().then(setTrips).catch(() => setTrips([]));
    billingStatus().then(setSub).catch(() => {});
  }, [user]);

  const onDelete = async (trip) => {
    const warn = trip.pdf_unlocked ? t(" Hai già sbloccato il PDF di questo viaggio: non sarà più disponibile.") : "";
    if (!window.confirm(`${t("Eliminare questo viaggio? Anche il link condivisibile smetterà di funzionare.")}${warn}`)) return;
    try { await deleteTrip(trip.id); setTrips((ts) => ts.filter((x) => x.id !== trip.id)); toast.success(t("Viaggio eliminato")); }
    catch (e) { toast.error(t(apiError(e))); }
  };

  return (
    <PageShell testId="account-page" eyebrow={t("Il tuo profilo")} title={user ? t("Ciao {name}, ecco i tuoi viaggi", { name: user.name }) : t("I miei viaggi")}>
      {user === null && <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />}
      {user === false && (
        <div data-testid="account-login-required" className="rounded-3xl border border-emerald-100 bg-white p-10">
          <p className="font-display text-2xl font-semibold text-gray-900">{t("Accedi per vedere i tuoi viaggi salvati")}</p>
          <button data-testid="account-login-button" onClick={() => openAuth()} className="mt-6 rounded-full bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700">{t("Accedi o registrati")}</button>
        </div>
      )}
      {user && (
        <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
          <div>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-gray-900">{t("I miei viaggi")} {trips && <span className="text-gray-400">({trips.length})</span>}</h2>
              <Link to="/#crea-viaggio" data-testid="new-trip-link" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"><Plus className="h-4 w-4" />{t("Nuovo viaggio")}</Link>
            </div>
            {trips?.length > 0 && <TisTracker trips={trips} />}
            {trips === null && <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />}
            {trips?.length === 0 && (
              <div data-testid="my-trips-empty" className="rounded-3xl border border-dashed border-emerald-200 bg-white p-10 text-gray-600">
                {t("Non hai ancora salvato nessun viaggio.")} <Link to="/#crea-viaggio" className="font-semibold text-emerald-700">{t("Creane uno ora")}</Link>.
              </div>
            )}
            <div data-testid="my-trips-list" className="grid gap-5 md:grid-cols-2">
              {trips?.map((trip) => <TripCard key={trip.id} t={trip} onDelete={onDelete} />)}
            </div>
          </div>
          <div className="space-y-5">
            {sub?.paywall !== false && <SubscriptionCard sub={sub} onChange={setSub} />}
            <div className="rounded-3xl bg-white p-6 text-sm text-gray-600 ring-1 ring-emerald-100">{t("Account")}: <strong className="text-gray-900">{user.email}</strong></div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
